import { StatusBadge } from '@/components/admin/entretiens/status-badge';
import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import {
  briefLevels,
  briefStyleTitle,
  candidateReadings,
  citationsSentence,
  eliminatedLine,
  noteRows,
} from '@/lib/session-analysis';
import { cn } from '@/lib/utils';
import { type NoyauAnalysis, type NoyauDetermination, noyauOf } from '@matter_extractor/shared';

/**
 * What a FOLDED determination says under its header (refonte-modulaire.md §12.2 « Depuis #224 » ;
 * noyau.md §9.1 ; maquette `Vue de session`, `#noyau`): three columns, so the block can be read shut.
 *
 * - **Niveaux** - Pourquoi, Comment, Quoi: fixed on a function or not, with the parts the code read
 *   for a level it did not fix, and the noyaux the levels eliminated;
 * - **Passe 2 · le style** - one line per candidate: its name and triplet, its note out of 100, its
 *   style, how many passages support it and how many contradict it, and its sentence in italics;
 * - **Les six notes** - compatibility, style, the bar and the note, the eliminated ones greyed.
 *
 * ## Nothing is derived here
 *
 * Every figure is read off the `analysis` the determination stored, through the same functions the
 * open block uses (`levelRows`, `noteRows`, `eliminationOf` of `lib/session-analysis.ts`): the code
 * that fixed the levels, eliminated the noyaux and noted the styles ran on the server.
 *
 * ## Only where there is something to read
 *
 * `hasBrief` (in the walk) decides: a rendered determination in two passes whose second pass
 * rendered. A pending run, a failure, an empty matter, a style absent and a reading of before #196
 * keep the line they always said, with no empty column drawn in its place. Open, the block draws
 * its sections and not this summary: the six notes are not said twice.
 */

export interface WalkNoyauBriefProps {
  readonly determination: NoyauDetermination;
  readonly analysis: NoyauAnalysis;
}

export function WalkNoyauBrief({ determination, analysis }: WalkNoyauBriefProps) {
  const notes = analysis.style?.state === 'rendered' ? analysis.style.notes : [];
  const eliminated = eliminatedLine(analysis);
  const rows = noteRows(determination, analysis);
  return (
    <div
      data-testid={SESSIONS_TEST_IDS.walkNoyauBrief}
      className="flex flex-wrap items-start gap-x-10 gap-y-4 px-4 pb-3"
    >
      <section aria-label="Niveaux" className="flex flex-col gap-1 text-caption sm:w-60">
        <h4 className="text-gray-600 text-caption uppercase">Niveaux</h4>
        {briefLevels(analysis).map((level) => (
          <p
            key={level.level}
            data-testid={SESSIONS_TEST_IDS.walkNoyauBriefLevel}
            data-level={level.level}
            data-state={level.state}
          >
            {level.label}{' '}
            {level.fixedOn === null ? (
              <span className="text-gray-600">
                {level.word}
                {level.detail === null ? '' : ` (${level.detail})`}
              </span>
            ) : (
              <>
                <b className="font-semibold text-gray-900">{level.fixedOn}</b>{' '}
                <StatusBadge tone="success" size="xs">
                  fixé
                </StatusBadge>
              </>
            )}
          </p>
        ))}
        {eliminated === null ? null : (
          <p className="mt-1 text-gray-600 text-caption">Éliminés : {eliminated}</p>
        )}
      </section>

      <section
        aria-label="Passe 2, le style"
        className="flex min-w-0 max-w-130 flex-1 basis-85 flex-col gap-2.5 text-caption"
      >
        <h4 className="text-gray-600 text-caption uppercase">{briefStyleTitle(analysis)}</h4>
        {notes.map((note) => {
          const noyau = noyauOf(note.noyau);
          // Read several times (#229): the note of each reading, then the one that carries the note.
          const readings = candidateReadings(analysis, note);
          const shown = readings === null ? note : readings.carrier.note;
          return (
            <div
              key={note.noyau}
              data-testid={SESSIONS_TEST_IDS.walkNoyauBriefStyle}
              data-noyau={note.noyau}
              className="flex flex-col gap-0.5"
            >
              <p className="flex items-baseline gap-2">
                <span
                  aria-hidden="true"
                  className="size-2 shrink-0 rounded-full"
                  style={{ backgroundColor: noyau.color }}
                />
                <span className="min-w-0 flex-1">
                  <b className="font-semibold text-gray-900">{noyau.name}</b>{' '}
                  <span className="text-gray-600 text-caption">{note.noyau}</span>
                </span>
                <span className="whitespace-nowrap tabular-nums">
                  <b className="font-bold text-body text-gray-900">{note.score}</b>
                  <span className="text-gray-600 text-caption"> / 100</span>
                </span>
              </p>
              <p className="pl-4 text-gray-700">
                {note.styleName === '' ? null : (
                  <>
                    style <b className="font-semibold text-gray-900">{note.styleName}</b> ·{' '}
                  </>
                )}
                {readings === null ? null : (
                  <>
                    <span data-testid={SESSIONS_TEST_IDS.walkNoyauBriefReadings}>
                      {readings.scores} · lecture {readings.carrier.run}
                    </span>
                    {' : '}
                  </>
                )}
                {citationsSentence(shown.supporting.length, shown.contradicting.length)}
              </p>
              <p className="pl-4 text-bleu-nuit italic leading-normal">{shown.justification}</p>
            </div>
          );
        })}
      </section>

      <section
        aria-label="Les six notes"
        className="flex min-w-0 max-w-135 flex-1 basis-100 flex-col gap-1 text-caption"
      >
        <div className={cn(NOTES_ROW, 'text-gray-600 text-caption')}>
          <span className="uppercase">Les six notes</span>
          <span className="text-right">compat.</span>
          <span className="text-right">style</span>
          <span aria-hidden="true" />
          <span className="text-right">note</span>
        </div>
        {rows.map((row) => {
          const noyau = noyauOf(row.noyau);
          return (
            <div
              key={row.noyau}
              data-testid={SESSIONS_TEST_IDS.walkNoyauBriefNote}
              data-noyau={row.noyau}
              data-place={row.place ?? undefined}
              data-eliminated={row.eliminated ? 'true' : undefined}
              className={cn(NOTES_ROW, row.eliminated ? 'text-gray-600' : 'text-gray-900')}
            >
              <span className="min-w-0">
                <span
                  aria-hidden="true"
                  className="mr-1.5 inline-block size-2 rounded-full"
                  style={{ backgroundColor: noyau.color }}
                />
                {noyau.name} <span className="text-gray-600 text-caption">{row.noyau}</span>
              </span>
              <span className="text-right tabular-nums">{row.compatibility ?? '-'}</span>
              <span className="text-right tabular-nums">
                {row.style === null ? '-' : row.style}
              </span>
              <span
                aria-hidden="true"
                className="block h-1.5 overflow-hidden rounded-full bg-gray-100"
              >
                <span
                  className="block h-full rounded-full"
                  style={{
                    width: `${row.score}%`,
                    backgroundColor: row.eliminated ? 'var(--color-gray-300)' : noyau.color,
                  }}
                />
              </span>
              <b className="text-right font-bold tabular-nums">{row.score}</b>
            </div>
          );
        })}
      </section>
    </div>
  );
}

/** The grid of a row of the third column: the name, compatibility, style, the bar, the note. */
const NOTES_ROW = 'grid grid-cols-[minmax(0,1fr)_3rem_3rem_5rem_2rem] items-center gap-x-2';
